import React from 'react'
// libs
import { useLazyQuery, useMutation } from '@apollo/client/react'
// hooks
import { useModalData } from 'hooks/index'
// graphql
import { RISKS_QUERY } from 'graphql/risk/queries'
import { REQUEST_AI_ASSISTANCE_MUTATION, RISK_CLONE_MUTATION, RISK_CREATE_MUTATION } from 'graphql/risk/mutations'
// components
import { Modal } from 'components/UI'
import { CloseIcon } from 'components/UI/Icons'
import Chip from '@mui/material/Chip'
import AiPrompt from 'components/Modals/AiFieldValueModal/components/AiPrompt'
import AiResultActions from 'components/Modals/AiFieldValueModal/components/AiResultActions'
// view components
import RisksSuggestions from 'components/Modals/AiLinkedFormsModal/components/RisksSuggestions'
// views
import { ChipContent, Content, Disclaimer, Header, SelectionRow } from './views'
// constants
import { Modals } from 'constants/modals'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { IAiAssistResponse, IAiSuggestedRecord, IFieldLink, IRisk, IRiskResponse } from 'types/graphql'
// utils
import { getFormFieldValue } from 'utils/form'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type Step = 'prompt' | 'result'
type AiAssistanceResponse = { requestAiAssistance: IAiAssistResponse }
type RiskCreateResponse = { riskCreate: IRiskResponse }
type RiskCloneResponse = { riskClone: IRiskResponse }
type SuggestionWithId = IAiSuggestedRecord & { id: string }
type SelectedId = { id: string; action: 'copy' | 'reference' }

const AiLinkedFormsModal: React.FC = () => {
  const { linkedRiskTemplates } = React.useContext(RiskTemplateAPIContext)
  const { risk, riskRefetch, refetchLinkedRisksPermissions } = React.useContext(RiskAPIContext)

  const [step, setStep] = React.useState<Step>('prompt')
  const [loading, setLoading] = React.useState(false)
  const [templateId, setTemplateId] = React.useState<string | null>(null)
  const [suggestions, setSuggestions] = React.useState<SuggestionWithId[]>([])
  const [selectedIds, setSelectedIds] = React.useState<SelectedId[]>([])

  const abortControllerRef = React.useRef<AbortController | null>(null)

  const [getRisk] = useLazyQuery<any>(RISKS_QUERY)
  const [cloneRisk] = useMutation<RiskCloneResponse>(RISK_CLONE_MUTATION)
  const [createRisk] = useMutation<RiskCreateResponse>(RISK_CREATE_MUTATION)
  const [requestAiAssistance] = useMutation<AiAssistanceResponse>(REQUEST_AI_ASSISTANCE_MUTATION)

  const {
    hideModal,
    modalData: { isOpen, fieldLink, contextRecordId, addRow, tableFieldLinks }
  } = useModalData<{
    addRow: (risk?: IRisk) => void
    fieldLink: IFieldLink
    contextRecordId: string
    tableFieldLinks: IFieldLink[]
  }>({
    modalType: Modals.AI_LINKED_FORMS_MODAL
  })

  React.useEffect(() => {
    if (!isOpen) {
      abortControllerRef.current?.abort()
      setStep('prompt')
      setSuggestions([])
      setSelectedIds([])
      setTemplateId(null)
    }
  }, [isOpen])

  const fields = linkedRiskTemplates?.find((item) => item.id === templateId)?.fields?.filter((item) => item.active)

  // show only the fields that are presented as columns in the table the modal was opened from
  const visibleFields = React.useMemo(() => {
    if (!fields) {
      return []
    }

    return (tableFieldLinks ?? [])
      .filter((item) => item.field?.entityId === templateId)
      .sort((a, b) => a.position - b.position)
      .map((item) => fields.find(({ id }) => id === item.field.id))
  }, [fields, tableFieldLinks, templateId])

  const requestAssistance = async (prompt: string) => {
    const controller = new AbortController()
    abortControllerRef.current = controller

    setLoading(true)

    try {
      const { data } = await requestAiAssistance({
        variables: { input: { fieldLinkId: fieldLink.id, userMessage: prompt, contextRecordId } },
        context: { fetchOptions: { signal: controller.signal } }
      })

      const newSuggestions = (data.requestAiAssistance.suggestions ?? []).map((suggestion) => ({
        ...suggestion,
        id: crypto.randomUUID()
      }))

      setSuggestions(newSuggestions)
      setSelectedIds([])
      setTemplateId(data.requestAiAssistance.templateId)
      setStep('result')
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error('[AiLinkedFormsModal.requestAiAssistance]: ', parseGraphQLErrorMessages(error))
        hideModal()
      }
    } finally {
      setLoading(false)
      abortControllerRef.current = null
    }
  }

  const onButtonClick = (id: string, action: 'copy' | 'reference') => {
    setSelectedIds((prev) => {
      const current = prev.find((item) => item.id === id)

      if (current) {
        if (current.action === action) {
          // Same action → deselect
          return prev.filter((item) => item.id !== id)
        }
        // Different action → switch
        return prev.map((item) => (item.id === id ? { ...item, action } : item))
      }

      return [...prev, { id, action }]
    })
  }

  const onApplyClick = async () => {
    setLoading(true)

    const updatedSuggestions = suggestions
      .filter((suggestion) => selectedIds.some((item) => item.id === suggestion.id))
      .map((suggestion) => ({
        ...suggestion,
        action: selectedIds.find((item) => item.id === suggestion.id)!.action,
        fields: suggestion.fields.map(({ fieldId, value }) => ({
          fieldId,
          value: getFormFieldValue(
            fields?.find(({ id }) => id === fieldId),
            value
          )
        }))
      }))

    try {
      const referenceIds = updatedSuggestions
        .filter(({ action, existingRecordId }) => action === 'reference' && existingRecordId)
        .map(({ existingRecordId }) => existingRecordId)

      let existingRisksMap: Record<string, IRisk> = {}

      if (referenceIds.length) {
        const { data } = await getRisk({
          variables: { filter: { ids: referenceIds, riskTemplateId: fieldLink.field.entityId } }
        })

        existingRisksMap = Object.fromEntries((data?.risks?.data ?? []).map((r: IRisk) => [r.id, r]))
      }

      await Promise.all(
        updatedSuggestions.map(async (item) => {
          let linkedRisk

          if (item.action === 'reference' && item.existingRecordId) {
            linkedRisk = existingRisksMap[item.existingRecordId]
          } else if (item.action === 'copy' && item.existingRecordId) {
            const {
              data: {
                riskClone: { data }
              }
            } = await cloneRisk({
              variables: {
                id: item.existingRecordId,
                input: { fieldLinkId: fieldLink.id, parentRiskId: risk.id }
              }
            })

            linkedRisk = data
          } else {
            const {
              data: {
                riskCreate: { data }
              }
            } = await createRisk({
              variables: {
                input: {
                  fieldLinkId: fieldLink.id,
                  parentRiskId: risk.id,
                  riskTemplateId: fieldLink.field.entityId,
                  fieldValues: item.fields.map(({ value, fieldId }) => ({ fieldId, value }))
                }
              }
            })

            linkedRisk = data
          }

          await addRow(linkedRisk)
        })
      )

      await refetchLinkedRisksPermissions()
      await riskRefetch()
    } catch (error) {
      console.error('[AiLinkedFormsModal.onApplyClick]: ', parseGraphQLErrorMessages(error))
    } finally {
      setLoading(false)
    }

    hideModal()
  }

  const onTryAgain = () => {
    setStep('prompt')
    setSuggestions([])
    setSelectedIds([])
    setTemplateId(null)
  }

  const onCancel = () => {
    abortControllerRef.current?.abort()
    hideModal()
  }

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      hideModal={onCancel}
      width={step === 'prompt' ? 'sm' : undefined}
      PaperProps={step === 'result' ? { style: { width: '90%', minWidth: '90%' } } : undefined}>
      <Content>
        <Header>
          AI-Powered Assessment
          <CloseIcon onClick={onCancel} />
        </Header>
        {step === 'prompt' && !loading && (
          <AiPrompt
            onCancel={onCancel}
            onConfirm={requestAssistance}
            description={`Describe the context to help rAI suggest relevant linked "${fieldLink.fieldLabel}" (optional)`}
            placeholder='e.g. We are assessing cloud infrastructure risks for a financial services'
          />
        )}
        {step === 'result' && (
          <>
            {!!suggestions.length && (
              <>
                <SelectionRow>
                  {!!selectedIds.length && (
                    <ChipContent>
                      <Chip label={selectedIds.length} size='small' onDelete={() => setSelectedIds([])} />
                      {`item${selectedIds.length > 1 ? 's' : ''} selected`}
                    </ChipContent>
                  )}
                </SelectionRow>
                <RisksSuggestions
                  fields={visibleFields}
                  suggestions={suggestions}
                  selectedIds={selectedIds}
                  onButtonClick={onButtonClick}
                />
              </>
            )}
            <Disclaimer>rAI can make mistakes. Check important info.</Disclaimer>
          </>
        )}
        {(loading || step === 'result') && (
          <AiResultActions
            loading={loading && step === 'prompt'}
            onCancel={onCancel}
            onApply={onApplyClick}
            onTryAgain={onTryAgain}
            applyDisabled={loading || selectedIds.length === 0}
          />
        )}
      </Content>
    </Modal>
  )
}

export default AiLinkedFormsModal
